import { SearchOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDebouncedCallback, useThrottledCallback } from 'use-debounce';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Transition from '@/components/Common/Transition';
import keyboardNavigatorUtils from '@/components/KeyboardNavigator/keyboardNavigatorUtils';
import type {
  KeyboardNavigatorKeymap,
  SelectedNavigatorItem,
} from '@/components/KeyboardNavigator/types';
import AMP_KEYS from '@/configs/amp-track-keys';
import { CHART_FREQUENCY } from '@/entities/chart/model/constants';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import { useOnClickOutside } from '@/hooks/pure/useOnClickOutside';
import service from '@/services/index';
import { useToast } from '@/stores/pure/ToastStore';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

import AsyncSearchTargetView from './AsyncSearchTargetView';
import DefaultSearchTargetView from './DefaultSearchTargetView';
import {
  SEARCH_TARGET,
  SearchTargetNameMap,
  type SelectableItemT,
} from './searchQueryBuilderTypes';
import searchQueryUtils from './searchQueryUtils';
import useSearchQueryData from './useSearchQueryData';

const allowKeyList = [
  'ArrowDown',
  'ArrowUp',
  'ArrowRight',
  'ArrowLeft',
  'Enter',
];

type Props = {
  onSubmitCallback?: (alias: string) => void;
  isLoading?: boolean;
  sizeType?: 'm' | 'l';
  onOpenStateChange?: (isOpen: boolean) => void;
  from: 'home' | 'modal' | 'float';
  autoFocus?: boolean;
};

const SearchQueryBuilder = (props: Props) => {
  const { sizeType = 'l' } = props;
  const ref = useRef<HTMLDivElement>(null);
  const addToast = useToast();
  const [openState, setOpenState] = useState(false);
  const { toDetailPage } = useGlobalSearchModalAction();
  const debounceState = useRef(false);

  const [input, setInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');

  const [searchTarget, setSearchTarget] = useState(SEARCH_TARGET.DEFAULT);

  const [selectedItem, setSelectedItem] =
    useState<SelectedNavigatorItem | null>(null);

  const { data, isLoading, fullColumn } = useSearchQueryData({
    searchTarget,
    debouncedInput,
  });

  const searchForList: SelectableItemT[] = useMemo(() => {
    return [
      SEARCH_TARGET.ACCOUNT,
      SEARCH_TARGET.CONTACT,
      SEARCH_TARGET.REMARKS,
      SEARCH_TARGET.TOPIC,
    ].map((target) => {
      return {
        value: target as string,
        label: SearchTargetNameMap.get(target) || '',
        action: () => {
          setSearchTarget(target);
        },
      };
    });
  }, []);

  const debounce = useDebouncedCallback(async (value: string) => {
    if (!debounceState.current) return;
    setDebouncedInput(value);
    debounceState.current = false;
  }, 400);

  const submit = async (params: {
    query?: string;
    currFilterSetQuery: StructuredFilterQueryFormatT[];
    isLoading: boolean;
  }) => {
    const { currFilterSetQuery, isLoading, query } = params;
    if (isLoading) return;
    if (currFilterSetQuery.length === 0 && !query) {
      addToast('Please enter a search query', 'fail');
      return;
    }

    const title = searchQueryUtils.getSearchTitle(
      query,
      currFilterSetQuery,
      (key) => {
        const col = structuredFilterUtils.getFieldInfo(key, fullColumn);
        const fieldInfo = col?.fieldInfo;
        return fieldInfo?.columnName || key;
      },
    );

    const filter =
      structuredFilterUtils.buildStructuredFilter(currFilterSetQuery);

    const res = await service.globalSearch.submitSearch({
      title,
      query,
      filter: {
        ...filter,
        start_date: getTimezoneDayjs()
          .startOf('day')
          .subtractVal(12, 'month')
          .toJSON(),
        end_date: getTimezoneDayjs().startOf('day').addVal(1, 'day').toJSON(),
      },
      options: {
        trend_frequency: CHART_FREQUENCY.DAY,
      },
    });

    toDetailPage(res.alias);
    props.onSubmitCallback?.(res.alias);
    track(AMP_KEYS.SEARCH_SUBMIT, {
      from: props.from,
    });
  };

  const keymap: KeyboardNavigatorKeymap | null = useMemo(() => {
    if (input === '') return null;

    const initialize = () => {
      setInput('');
      debounceState.current = false;
      setDebouncedInput('');
      setSelectedItem(null);
      setSearchTarget(SEARCH_TARGET.DEFAULT);
    };

    if (searchTarget !== SEARCH_TARGET.DEFAULT) {
      return [
        [
          {
            index: 0,
            action: () => {
              initialize();
            },
          },
          ...(data?.map((item, index) => ({
            index: index + 1,
            action: () => {
              submit({
                currFilterSetQuery: [item],
                isLoading: false,
              });
              initialize();
            },
          })) || []),
        ],
      ];
    }

    const textSearchItems = [
      {
        index: 0,
        action: async () => {
          submit({
            query: input,
            currFilterSetQuery: [],
            isLoading: false,
          });

          initialize();
        },
      },
      {
        index: 1,
        action: async () => {
          submit({
            currFilterSetQuery: [
              {
                columnKey: FEEDBACK_COLUMNS.SNIPPET,
                operator: STRUCTURED_FILTER_OPERATOR.OR,
                filterValues: [
                  {
                    filterKey: input,
                    filterName: input,
                  },
                ],
              },
            ],
            isLoading: false,
          });

          initialize();
        },
      },
    ];

    const col0 = [
      ...textSearchItems,
      ...(data?.map((item, index) => ({
        index: index + textSearchItems.length,
        action: () => {
          submit({
            currFilterSetQuery: [item],
            isLoading: false,
          });
          initialize();
        },
      })) || []),
    ];
    const col1 = searchForList.map((item, index) => ({
      index,
      action: item.action,
    }));

    return [col0, col1];
  }, [input, data, searchForList, searchTarget]);

  const onInputKeyDown = useThrottledCallback(({ key, value }) => {
    keyboardNavigatorUtils.keyboardNavigationEventHandler({
      key,
      keyboardNavigatorKeymap: keymap,
      selectedItem,
      setSelectedItem,
      value,
    });
  }, 100);

  useEffect(() => {
    if (keymap === null || keymap.length === 0) return;
    setSelectedItem({
      col: 0,
      index: 0,
    });
  }, [keymap]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (allowKeyList.includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        onInputKeyDown({
          key: e.key,
          value: (e.target as HTMLInputElement).value,
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onInputKeyDown]);

  useEffect(() => {
    setOpenState(!!input);
    props.onOpenStateChange?.(!!input);
  }, [input, props.onOpenStateChange]);

  useOnClickOutside(ref, () => {
    setOpenState(false);
    props.onOpenStateChange?.(false);
  });

  return (
    <Style
      ref={ref}
      isLoading={props.isLoading}
      sizeType={sizeType}
      css={
        openState
          ? css`
              min-height: 400px;
              height: auto;
              box-shadow: 0px 4px 24px ${(props) => props.theme.colors.FD08};
            `
          : css`
              min-height: ${sizeType === 'l' ? '62px' : '48px'};
              height: ${sizeType === 'l' ? '62px' : '48px'};
            `
      }
    >
      <Flex
        g={sizeType === 'l' ? 10 : 8}
        a="center"
        className="search-input-area"
      >
        <AIIcon className="text-2xl" />
        <Flex className="input-wrapper" f={1} a="center">
          <input
            autoFocus={props.autoFocus}
            style={{ minWidth: 50, flex: 1, minHeight: 40 }}
            placeholder={'e.g. Complaints about new Yuzu flavor'}
            value={input}
            disabled={props.isLoading}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setInput(value);
              debounceState.current = true;
              debounce(value);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
              if (allowKeyList.includes(e.key)) {
                e.preventDefault();
                if (e.nativeEvent.isComposing) return;
                onInputKeyDown({
                  key: e.key,
                  value: e.currentTarget.value,
                });
              }
            }}
          />
        </Flex>
        <Button
          variant="ghost"
          sizeType={sizeType}
          isLoading={props.isLoading}
          onClick={() =>
            submit({
              query: input,
              currFilterSetQuery: [],
              isLoading,
            })
          }
          style={{
            width: sizeType === 'l' ? 40 : 32,
            borderRadius: '100%',
          }}
        >
          <SearchOutlined style={{ fontSize: 18 }} />
        </Button>
      </Flex>

      <Transition
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          height: 100%;
          overflow: auto;
        `}
        status={!!debouncedInput && !!input && openState}
      >
        <Divider />
        {keymap &&
          (searchTarget === SEARCH_TARGET.DEFAULT ? (
            <DefaultSearchTargetView
              searchForList={searchForList}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              keymap={keymap}
              debouncedInput={debouncedInput}
              data={data}
              isLoading={isLoading}
            />
          ) : (
            <AsyncSearchTargetView
              onBackClick={() => setSearchTarget(SEARCH_TARGET.DEFAULT)}
              targetName={SearchTargetNameMap.get(searchTarget) || ''}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              keymap={keymap}
              debouncedInput={debouncedInput}
              data={data}
              isLoading={isLoading}
            />
          ))}
      </Transition>
    </Style>
  );
};

const Style = styled(VFlex)<{
  isLoading?: boolean;
  sizeType?: 'm' | 'l';
}>`
  width: 100%;
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: ${(props) => props.theme.radius.token.xl}px;
  transition: all 0.2s ease-in-out;
  overflow: unset;
  border: 1px solid ${(props) => props.theme.colors.gray300};
  max-height: 600px;
  box-shadow: ${(props) => props.theme.shadow[1]};
  ${({ isLoading }) =>
    isLoading &&
    css`
      background-color: ${(props) => props.theme.colors.gray250};
    `}
  .search-input-area {
    padding: ${(props) =>
      props.sizeType === 'l' ? '10px 10px 10px 16px' : '3px 10px 3px 12px'};

    .input-wrapper {
      width: 100%;
      flex-wrap: wrap;
      min-width: 40px;
      align-items: center;
      input {
        width: 100%;
        border: none;
        background-color: transparent;
        height: 24px;
        color: ${(props) => props.theme.colors.gray800};
        font-size: ${(props) => (props.sizeType === 'l' ? '16px' : '15px')};
        &::placeholder {
          color: ${(props) => props.theme.colors.gray500};
        }
        &:focus {
          outline: none;
        }
      }
    }
  }
`;

export default SearchQueryBuilder;
